import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Users, Maximize, ArrowLeft, ChevronLeft, ChevronRight, Check, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { roomsApi } from '../services/api';
import { rooms as mockRooms } from '../mockData';
import { useSEO } from '../hooks/useSEO';

const RoomDetailPage = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currency, setCurrency] = useState('USD');

  const seoJsonLd = useMemo(() => room ? {
    '@context': 'https://schema.org',
    '@type': 'HotelRoom',
    name: room.name,
    description: room.description || room.shortDescription,
    occupancy: { '@type': 'QuantitativeValue', maxValue: room.occupancy?.max }
  } : null, [room]);

  useSEO({
    title: room ? `${room.name} | OlivOak Villa` : 'Room Details | OlivOak Villa',
    description: room ? `${room.name} at OlivOak Villa — ${room.shortDescription || room.description}. View photos, amenities, and book now.` : 'View room details and book your stay at OlivOak Villa.',
    jsonLd: seoJsonLd
  });

  const fetchRoom = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Try to fetch by slug first (roomId from URL is the slug)
      const data = await roomsApi.getBySlug(roomId);
      
      // Transform API data to match frontend format
      const transformedRoom = {
        id: data.slug || data.id,
        dbId: data.id,
        name: data.name,
        description: data.description,
        shortDescription: data.short_description,
        occupancy: { max: data.max_occupancy, beds: data.beds },
        size: `${data.size_sqm} sqm`,
        price: {
          USD: data.price_usd,
          ZAR: data.price_zar,
          GBP: data.price_gbp,
          EUR: data.price_eur
        },
        images: data.images || [],
        amenities: data.amenities || [],
        available: data.available
      };
      
      setRoom(transformedRoom);
    } catch (err) {
      console.error('Failed to fetch room:', err);
      
      // Fallback: Try to find in mock data
      const mockRoom = mockRooms.find((r) => r.id === roomId);
      if (mockRoom) {
        setRoom(mockRoom);
        setError('Using sample data');
      } else {
        setError('Room not found');
      }
    } finally {
      setLoading(false);
    }
  }, [roomId]);

  useEffect(() => {
    fetchRoom();
  }, [fetchRoom]);

  if (loading) {
    return (
      <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '120px' }}>
        <div className="flex items-center justify-center py-32">
          <Loader2 className="animate-spin" size={40} style={{ color: 'var(--olive-primary)' }} />
        </div>
      </div>
    );
  }

  if (!room) {
    return (
      <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '120px' }}>
        <div className="container text-center">
          <h1 className="heading-1 mb-4">Room Not Found</h1>
          <p className="body-regular mb-8" style={{ color: 'var(--text-secondary)' }}>
            The room you're looking for doesn't exist or has been removed.
          </p>
          <Link to="/rooms">
            <Button className="btn-primary" data-testid="back-to-rooms-btn">Back to Rooms</Button>
          </Link>
        </div>
      </div>
    );
  }

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % room.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + room.images.length) % room.images.length);
  };

  const getCurrencySymbol = (curr) => {
    const symbols = { USD: '$', ZAR: 'R', GBP: '£', EUR: '€' };
    return symbols[curr] || '$';
  };

  return (
    <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '80px' }}>
      <section className="py-12">
        <div className="container">
          {/* Back Button */}
          <button
            onClick={() => navigate('/rooms')}
            className="flex items-center gap-2 mb-8 btn-secondary"
            data-testid="back-button"
          >
            <ArrowLeft size={16} />
            Back to Rooms
          </button>

          {error && (
            <div className="mb-8 p-4" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                {error}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Image Gallery */}
            <div>
              <div className="relative">
                <img
                  src={room.images[currentImageIndex]}
                  alt={`${room.name} - Image ${currentImageIndex + 1}`}
                  className="w-full h-[500px] object-cover"
                  style={{ border: '1px solid var(--border-light)' }}
                  data-testid="room-main-image"
                />
                {room.images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 p-3"
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        border: '1px solid var(--border-light)',
                        color: 'var(--text-primary)'
                      }}
                      data-testid="prev-image-btn"
                    >
                      <ChevronLeft size={24} />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 p-3"
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        border: '1px solid var(--border-light)',
                        color: 'var(--text-primary)'
                      }}
                      data-testid="next-image-btn"
                    >
                      <ChevronRight size={24} />
                    </button>
                  </>
                )}
              </div>

              {/* Thumbnail Navigation */}
              {room.images.length > 1 && (
                <div className="flex gap-4 mt-4">
                  {room.images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className="hover-lift"
                      data-testid={`thumbnail-${index}`}
                    >
                      <img
                        src={image}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-24 h-24 object-cover"
                        style={{
                          border: index === currentImageIndex ? '2px solid var(--interactive-base)' : '1px solid var(--border-light)',
                          opacity: index === currentImageIndex ? 1 : 0.6
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Room Details */}
            <div>
              <h1 className="hero-medium mb-4" data-testid="room-name">{room.name}</h1>
              <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>
                {room.description}
              </p>

              {/* Room Specs */}
              <div
                className="grid grid-cols-2 gap-6 p-6 mb-8"
                style={{
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-light)'
                }}
              >
                <div>
                  <p className="body-small mb-2" style={{ color: 'var(--text-secondary)' }}>
                    Occupancy
                  </p>
                  <div className="flex items-center gap-2">
                    <Users size={20} style={{ color: 'var(--text-primary)' }} />
                    <span className="body-regular" data-testid="room-occupancy">
                      Up to {room.occupancy.max} guests
                    </span>
                  </div>
                </div>
                <div>
                  <p className="body-small mb-2" style={{ color: 'var(--text-secondary)' }}>
                    Room Size
                  </p>
                  <div className="flex items-center gap-2">
                    <Maximize size={20} style={{ color: 'var(--text-primary)' }} />
                    <span className="body-regular" data-testid="room-size">{room.size}</span>
                  </div>
                </div>
                <div className="col-span-2">
                  <p className="body-small mb-2" style={{ color: 'var(--text-secondary)' }}>
                    Bed Configuration
                  </p>
                  <span className="body-regular" data-testid="room-beds">{room.occupancy.beds}</span>
                </div>
              </div>

              {/* Amenities */}
              <div className="mb-8">
                <h3 className="heading-3 mb-4">Amenities</h3>
                <div className="grid grid-cols-2 gap-4">
                  {room.amenities.map((amenity) => (
                    <div key={amenity} className="flex items-center gap-2">
                      <Check size={16} style={{ color: 'var(--text-primary)' }} />
                      <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                        {amenity}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pricing & CTA */}
              <div
                className="p-6"
                style={{
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-light)'
                }}
              >
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="body-small mb-2" style={{ color: 'var(--text-secondary)' }}>
                      Price per night
                    </p>
                    <p className="heading-2" data-testid="currency-USD">
                      ${room.price['USD']}
                    </p>
                  </div>
                </div>
                <Link to={`/booking?room=${room.id}`}>
                  <Button className="btn-primary w-full" data-testid="book-room-btn">Book This Room</Button>
                </Link>
                <p className="text-meta mt-4 text-center" style={{ color: 'var(--text-light)' }}>
                  2% ZTA tax applied at checkout
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RoomDetailPage;
